import { EXTERNAL_CALL_URL_MAX, LESSON_DURATION_MAX, LESSON_DURATION_MIN } from "@edu/shared";
import { weekdayName } from "./common.ts";
import { GROUP_WEEKLY_ONLY } from "./group-text.ts";

/**
 * Два поля диалога создания занятия, у которых своя логика: повторение и ссылка
 * на созвон.
 *
 * Отдельным файлом с 30.08.2026, и повод тот же, что у `OccurrenceRecipient.tsx`
 * тремя днями раньше: диалог перерос четыреста строк (раздел 9 CLAUDE.md), когда
 * форма стала уступать место двери на экран расписания группы. Резать по полям
 * законно ровно у этих двух: у каждого есть своя ветка (заперто у группы, домен
 * под ссылкой), а у даты, названия и заготовки её нет, и вынос превратил бы их в
 * пересылку значения туда и обратно.
 *
 * Состояние остаётся у диалога целиком: сюда приезжают значения, отсюда уезжают
 * события. Своей памяти у полей нет намеренно, иначе набранное терялось бы при
 * каждом переключении адресата.
 */

/**
 * Разовое занятие или еженедельное.
 *
 * ⚠️ **У группы выбор заперт**, потому что разового группового занятия сервер не
 * умеет вовсе (решение владельца 30.08.2026, раздел 9
 * `docs/group-lessons-design.md`). Запрет при этом объясняется словами, а не
 * только запертым переключателем: человек, пришедший поставить группе
 * дополнительное занятие перед пробником, узнал бы иначе только запрет и не
 * узнал бы, что делать.
 */
export function RepeatChoice({
  weekly,
  locked,
  weekday,
  onPick,
}: {
  weekly: boolean;
  locked: boolean;
  /** День недели по ISO: у еженедельного он выведен из даты нажатия. */
  weekday: number;
  onPick: (repeat: "single" | "weekly") => void;
}) {
  return (
    <>
      <fieldset className="field">
        <legend className="lbl">Повторение</legend>
        <label className="form__check">
          <input
            type="radio"
            name="repeat"
            checked={!weekly}
            disabled={locked}
            onChange={() => onPick("single")}
          />
          разовое занятие
        </label>
        <label className="form__check">
          <input
            type="radio"
            name="repeat"
            checked={weekly}
            disabled={locked}
            onChange={() => onPick("weekly")}
          />
          каждую неделю, {weekdayName(weekday)}
        </label>
      </fieldset>
      {locked && <p className="hint">{GROUP_WEEKLY_ONLY}</p>}
    </>
  );
}

/**
 * Ссылка на созвон в чужом сервисе.
 *
 * Стоит рядом с названием, а не в конце и не под раскрывашкой: для
 * преподавателя, ведущего в Zoom, это такое же поле занятия, как время. Прятать
 * его поглубже запрещено решением владельца 20.08.2026 (раздел 7
 * `docs/switching-costs.md`): так теряется не комната, а расписание.
 *
 * ⚠️ Домен показываем рядом, как и везде, где эта ссылка живёт: белого списка
 * доменов у нас нет намеренно, и домен это единственное, что мы про чужой адрес
 * говорим. Читает его в том числе несовершеннолетний ученик, когда занятие
 * появится в его расписании. Отказ формы называем ДО отправки: круг по сети на
 * 128 кбит/с ради «адрес не тот» это несколько секунд, за которые человек
 * решает, что ничего не работает.
 *
 * ⚠️ Домен приезжает СЧИТАННЫМ, а не считается здесь: тем же значением диалог
 * гасит главную кнопку, и второй разбор адреса разошёлся бы с первым.
 */
export function CallLinkField({
  value,
  host,
  onChange,
}: {
  value: string;
  /** Домен разобранного адреса, либо `null`, если адрес не разбирается. */
  host: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <>
      <label className="field">
        <span className="lbl">Ссылка на созвон</span>
        <input
          className="fld"
          type="url"
          value={value}
          maxLength={EXTERNAL_CALL_URL_MAX}
          placeholder="https://… Zoom, Телемост, Meet"
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      {value.trim() !== "" &&
        (host !== null ? (
          <p className="hint">Ученик увидит кнопку «Созвон» и подпись {host}</p>
        ) : (
          <p className="hint hint--err">
            Нужен полный адрес, начинающийся с https:// и без пробелов
          </p>
        ))}
    </>
  );
}

/**
 * Дата, время и длительность одной строкой.
 *
 * Вынесены 27.09.2026, когда у диалога появился второй вид, «Открыть для
 * записи»: эти три поля у обоих видов одни и те же и обязаны вести себя
 * одинаково, а диалог и так стоял за четырьмястами строками. Значение живёт у
 * диалога, поэтому при смене вида набранное остаётся.
 */
export function WhenFields({
  date,
  time,
  durationMin,
  onDate,
  onTime,
  onDuration,
}: {
  date: string;
  time: string;
  durationMin: number;
  onDate: (date: string) => void;
  onTime: (time: string) => void;
  onDuration: (minutes: number) => void;
}) {
  return (
    <div className="form form--row">
      <label className="field">
        <span className="lbl">Дата</span>
        <input
          className="fld"
          type="date"
          value={date}
          onChange={(e) => onDate(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="lbl">Время</span>
        <input
          className="fld"
          type="time"
          value={time}
          onChange={(e) => onTime(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="lbl">Минут</span>
        <input
          className="fld"
          type="number"
          min={LESSON_DURATION_MIN}
          max={LESSON_DURATION_MAX}
          value={durationMin}
          onChange={(e) => onDuration(Number(e.target.value))}
        />
      </label>
    </div>
  );
}
